import React, { useState } from 'react';
import { Button, ButtonProps } from '../button/index';

export interface PromiseButtonProps extends Omit<ButtonProps, 'onClick' | 'href' | 'loading'> {
	/** The function to be called when an onClick event is triggered. It must return a Promise. */
	onClick: (e: React.MouseEvent<HTMLButtonElement>) => Promise<unknown>;
	/** An optional function to be called when a promise is rejected. Note that the `reason`
	 * argument returned by a promise rejection could be of `any` type. */
	onReject?: (reason: unknown) => void;
}

/**
 * PromiseButton is a convenience component, specifically for `onClick` actions that return Promises. The built in `try / catch`
 * block will call the onClick function if a promise resolves or, in the event of a rejection, will call the function passed to the `onReject` prop.
 * PromiseButton will also render the button in a loading state while waiting for a promise to resolve, meaning the button will be
 * disabled and display a loading spinner.
 */
export const PromiseButton = React.forwardRef<HTMLButtonElement, PromiseButtonProps>((props, ref) => {
	const { onClick, onReject, ...rest } = props;
	const [loading, setLoading] = useState(false);
	return (
		<Button
			{...rest}
			loading={loading}
			ref={ref}
			onClick={async event => {
				setLoading(true);
				try {
					await onClick(event);
					setLoading(false);
					return;
				} catch (error) {
					onReject?.(error);
					setLoading(false);
					return;
				}
			}}
		/>
	);
});
